import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { backgroundChatKeys } from '~/lib/query-keys';

export type BackgroundChatStatus = 'running' | 'completed' | 'failed' | 'cancelled';

export interface BackgroundChat {
  id: number;
  zitadelOrgId: string;
  userId: string;
  sessionId: string;
  runId: string;
  title: string;
  status: BackgroundChatStatus;
  startedAt: string;
  completedAt?: string;
  answerMessageId?: string;
  errorMessage?: string;
}

interface ListResponse {
  runs: BackgroundChat[];
}

interface RunResponse {
  run: BackgroundChat | null;
}

function isAuthError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 401;
}

/**
 * Recent background inbox-chat runs (running + terminal) for the current
 * org+user, newest first — drives the Inspector's Background Tasks cards. The
 * SSE handler invalidates `backgroundChatKeys.all` on `background-chat-changed`
 * so this stays live; we also poll while any run is still `running` as a
 * fallback for the source-of-truth Postgres row.
 */
export function useBackgroundChats() {
  return useQuery({
    queryKey: backgroundChatKeys.list(),
    queryFn: async () => {
      const data = await api.get<ListResponse>('/api/background-chats');
      return data.runs;
    },
    refetchInterval: (q) => {
      if (isAuthError(q.state.error)) return false;
      const runs = q.state.data as BackgroundChat[] | undefined;
      return runs?.some((r) => r.status === 'running') ? 2_000 : false;
    },
    retry: (_count, err) => !isAuthError(err),
  });
}

/**
 * Latest background-chat run for one inbox session, or null — drives the
 * inbox-row status dot and (FRONT-2195) the reopened-modal's composer
 * Stop/Send + busy state (`isQueueBusy`, `turn-busy.ts`) and its
 * button-action spinner (`backgroundActive`) — there is no separate "still
 * working" banner any more; the in-progress answer itself is re-attached
 * live instead. Also (FRONT-2185) the operator panel's Chat tab's own
 * `queueBusy`/`queuePending` wiring (`operator-chat-panel.tsx`), same
 * pattern as `inbox-chat-modal.tsx`/`day-one-chat.tsx`. Polls while running,
 * stops once terminal.
 */
export function useBackgroundChatForSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: sessionId
      ? backgroundChatKeys.bySession(sessionId)
      : backgroundChatKeys.all,
    queryFn: async () => {
      if (!sessionId) return null;
      const data = await api.get<RunResponse>(
        `/api/background-chats/by-session?sessionId=${encodeURIComponent(sessionId)}`,
      );
      return data.run;
    },
    enabled: !!sessionId,
    refetchInterval: (q) => {
      if (isAuthError(q.state.error)) return false;
      const run = q.state.data as BackgroundChat | null | undefined;
      return run?.status === 'running' ? 2_000 : false;
    },
    retry: (_count, err) => !isAuthError(err),
  });
}
